<template>
  <div>
    <Nav></Nav>
    <b-container fluid="xl">
      <b-row class="mt-3">
        <b-col><h1>Configuración → Ticket impreso</h1></b-col>
      </b-row>
      <b-row>
        <b-col>
          <p class="text-muted">
            Líneas de texto, QRs e imágenes que se agregan al ticket de venta impreso en el POS.
            Se sincronizan a las sucursales en el siguiente sync del POS. Lo configurado en la
            sucursal (facturación, QR extra, últimas líneas) se sigue imprimiendo igual.
          </p>
        </b-col>
      </b-row>

      <b-row class="mb-2">
        <b-col md="4">
          <vu-select v-model="filterSucursal" :options="sucursalPickOptions" :reduce="o => o.value" label="text" placeholder="Filtrar por sucursal"></vu-select>
        </b-col>
        <b-col class="text-right">
          <b-button v-if="hasPermission('rrn:central:ticketPrintBlocks:create')" variant="primary" @click="openNew">Nuevo bloque</b-button>
        </b-col>
      </b-row>

      <div v-for="pos in positionOptions" :key="pos.value" class="mb-4">
        <h5>{{ pos.text }}</h5>
        <b-table small striped hover responsive :items="blocksByPosition(pos.value)" :fields="fields" :busy="loading" show-empty empty-text="Sin bloques">
          <template #cell(block_type)="data">{{ typeLabel(data.item.block_type) }}</template>
          <template #cell(content)="data">
            <small><code>{{ summary(data.item) }}</code></small>
          </template>
          <template #cell(applies)="data">{{ appliesLabel(data.item) }}</template>
          <template #cell(buttons)="data">
            <b-button v-if="hasPermission('rrn:central:ticketPrintBlocks:update')" size="sm" variant="warning" class="mr-1" @click="openEdit(data.item)">Editar</b-button>
            <b-button v-if="hasPermission('rrn:central:ticketPrintBlocks:delete')" size="sm" variant="danger" @click="remove(data.item)">Eliminar</b-button>
          </template>
        </b-table>
      </div>
    </b-container>

    <b-modal id="modal-print-block" size="xl" :title="form.id ? 'Editar bloque' : 'Nuevo bloque'" @ok.prevent="save" ok-title="Guardar" cancel-title="Cancelar" :ok-disabled="saving">
      <b-row>
        <b-col lg="7">
          <b-form-group label="Nombre (interno)">
            <b-form-input v-model="form.name" maxlength="100" placeholder="Ej. QR encuesta de servicio"></b-form-input>
          </b-form-group>
          <b-row>
            <b-col><b-form-group label="Tipo"><b-form-select v-model="form.block_type" :options="typeOptions"></b-form-select></b-form-group></b-col>
            <b-col><b-form-group label="Posición"><b-form-select v-model="form.position" :options="positionOptions"></b-form-select></b-form-group></b-col>
            <b-col md="2"><b-form-group label="Orden"><b-form-input v-model.number="form.sort_order" type="number"></b-form-input></b-form-group></b-col>
          </b-row>

          <b-form-group v-if="form.block_type === 'text'" label="Texto" description="Una línea por renglón. Puedes usar variables.">
            <b-form-textarea ref="content" v-model="form.content" rows="3"></b-form-textarea>
          </b-form-group>
          <b-form-group v-if="form.block_type === 'qr'" label="Contenido del QR (URL o texto)" description="Las variables se codifican para URL.">
            <b-form-input ref="content" v-model="form.content" placeholder="https://encuesta.ejemplo.com/?s={sucursal_id}&t={folio}"></b-form-input>
          </b-form-group>
          <div v-if="form.block_type === 'text' || form.block_type === 'qr'" class="mb-3">
            <small class="text-muted mr-1">Variables:</small>
            <b-badge v-for="ph in placeholders" :key="ph.key" variant="light" class="mr-1 mb-1 placeholder-chip" :title="ph.text" @click="insertPlaceholder(ph.key)">{{ ph.key }}</b-badge>
          </div>

          <b-form-group v-if="form.block_type === 'image'" label="Imagen" :description="'PNG o JPG, máximo ' + maxImageWidth + 'px de ancho. Se imprime en blanco y negro.'">
            <b-form-file accept="image/png,image/jpeg" placeholder="Selecciona una imagen" @input="uploadImage"></b-form-file>
            <div v-if="uploading" class="small text-muted mt-1">Subiendo…</div>
            <img v-if="form.image_url" :src="form.image_url" class="mt-2 img-preview">
          </b-form-group>

          <b-form-group v-if="form.block_type === 'qr' || form.block_type === 'image'" label="Texto arriba (opcional)">
            <b-form-input v-model="form.caption" maxlength="255"></b-form-input>
          </b-form-group>
          <b-form-group v-if="form.block_type === 'qr' || form.block_type === 'image'" label="Texto alterno" description="Se imprime si la impresora no soporta QR/imágenes.">
            <b-form-input v-model="form.fallback_text" maxlength="255"></b-form-input>
          </b-form-group>

          <b-row v-if="form.block_type !== 'separator' && form.block_type !== 'blank'">
            <b-col><b-form-group label="Alineación"><b-form-select v-model="form.align" :options="alignOptions"></b-form-select></b-form-group></b-col>
            <b-col v-if="form.block_type === 'text'" class="pt-4">
              <b-form-checkbox v-model="form.bold" switch>Negritas</b-form-checkbox>
              <b-form-checkbox v-model="form.double_size" switch>Doble tamaño</b-form-checkbox>
            </b-col>
          </b-row>

          <b-form-group label="Imprimir">
            <b-form-select v-model="form.print_condition" :options="conditionOptions"></b-form-select>
          </b-form-group>
          <b-form-group label="Sucursales" description="Deja vacío para todas. Selecciona para limitar a sucursales específicas.">
            <vu-select v-model="form.sucursalIds" :options="sucursalPickOptions" :reduce="o => o.value" label="text" multiple :close-on-select="false" placeholder="Todas las sucursales"></vu-select>
            <div class="small text-muted mt-1">
              {{ form.sucursalIds.length ? form.sucursalIds.length + ' sucursal(es) seleccionada(s)' : 'Todas las sucursales' }}
            </div>
          </b-form-group>
          <b-form-group label="Áreas" description="Deja vacío para todas las áreas.">
            <vu-select v-model="form.areaIds" :options="areaPickOptions" :reduce="o => o.value" label="text" multiple :close-on-select="false" placeholder="Todas las áreas"></vu-select>
          </b-form-group>
        </b-col>

        <b-col lg="5">
          <h6>Vista previa</h6>
          <b-form-radio-group v-model="previewWidth" :options="[{ value: 40, text: '40 col' }, { value: 48, text: '48 col' }]" size="sm" class="mb-2"></b-form-radio-group>
          <div class="ticket-preview" :style="{ width: previewWidth + 'ch' }">
            <div class="text-center">GRACIAS POR SU PREFERENCIA</div>
            <div v-if="form.block_type === 'separator'">{{ '='.repeat(previewWidth) }}</div>
            <div v-else-if="form.block_type === 'blank'">&nbsp;</div>
            <div v-else :style="previewStyle">
              <div v-if="form.caption && form.block_type !== 'text'">{{ resolve(form.caption, false) }}</div>
              <div v-if="form.block_type === 'text'" class="pre">{{ resolve(form.content, false) }}</div>
              <div v-if="form.block_type === 'qr'">
                <img v-if="qrPreview" :src="qrPreview" class="qr-preview">
                <div class="small text-muted text-break">{{ resolve(form.content, true) }}</div>
              </div>
              <img v-if="form.block_type === 'image' && form.image_url" :src="form.image_url" class="img-preview">
            </div>
          </div>
          <small class="text-muted">Datos de ejemplo: folio 1234, total 350.00, sucursal 1.</small>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import QRCode from "qrcode";
import Nav from "../../components/Nav.vue";
import { permissionMixin } from "../../mixins/permissionMixin";
import ticketPrintBlocksService from "../../services/ticketPrintBlocksService";

const emptyForm = () => ({
  id: null,
  name: "",
  block_type: "text",
  position: "footer",
  sort_order: 0,
  content: "",
  caption: "",
  image_url: "",
  image_hash: "",
  fallback_text: "",
  align: "center",
  bold: false,
  double_size: false,
  print_condition: "always",
  sucursalIds: [],
  areaIds: [],
});

export default {
  name: "ticket-print-blocks",
  components: { Nav },
  mixins: [permissionMixin],
  data() {
    return {
      blocks: [],
      sucursals: [],
      areas: [],
      loading: true,
      saving: false,
      uploading: false,
      filterSucursal: null,
      form: emptyForm(),
      previewWidth: 40,
      qrPreview: "",
      maxImageWidth: 576,
      fields: [
        { key: "sort_order", label: "Orden" },
        { key: "name", label: "Nombre" },
        { key: "block_type", label: "Tipo" },
        { key: "content", label: "Contenido" },
        { key: "applies", label: "Aplica a" },
        { key: "buttons", label: "" },
      ],
      positionOptions: [
        { value: "header", text: "Encabezado (después de datos de la empresa)" },
        { value: "before_products", text: "Antes de productos" },
        { value: "after_totals", text: "Después de totales" },
        { value: "footer", text: "Pie (al final del ticket)" },
      ],
      typeOptions: [
        { value: "text", text: "Texto" },
        { value: "qr", text: "QR" },
        { value: "image", text: "Imagen" },
        { value: "separator", text: "Separador" },
        { value: "blank", text: "Línea en blanco" },
      ],
      alignOptions: [
        { value: "left", text: "Izquierda" },
        { value: "center", text: "Centro" },
        { value: "right", text: "Derecha" },
      ],
      conditionOptions: [
        { value: "always", text: "Siempre" },
        { value: "paid", text: "Solo si la cuenta está pagada" },
        { value: "unpaid", text: "Solo si la cuenta no está pagada" },
      ],
      placeholders: [
        { key: "{folio}", text: "Folio del ticket", sample: "1234" },
        { key: "{ticket_id}", text: "Id local del ticket", sample: "987" },
        { key: "{client_uuid}", text: "UUID del ticket", sample: "3f2a-…" },
        { key: "{sucursal_id}", text: "Id de sucursal", sample: "1" },
        { key: "{sucursal_name}", text: "Nombre de sucursal", sample: "Centro" },
        { key: "{total}", text: "Total con descuento", sample: "350.00" },
        { key: "{subtotal}", text: "Total antes de descuento", sample: "380.00" },
        { key: "{tip}", text: "Propina", sample: "35.00" },
        { key: "{fecha}", text: "Fecha de apertura", sample: "2026-10-07 13:00:00" },
        { key: "{area}", text: "Área", sample: "Salón" },
        { key: "{mesa}", text: "Mesa", sample: "12" },
        { key: "{mesero}", text: "Mesero", sample: "Luis" },
        { key: "{customer_name}", text: "Cliente", sample: "Ana Pérez" },
        { key: "{tenant}", text: "Tenant de la empresa", sample: "empresa" },
      ],
    };
  },
  computed: {
    sucursalPickOptions() {
      return this.sucursals.map((s) => ({ value: s.id, text: s.name || s.nombre || "Sucursal " + s.id }));
    },
    areaPickOptions() {
      return this.areas.map((a) => ({ value: a.id, text: a.name || "Área " + a.id }));
    },
    previewStyle() {
      return {
        textAlign: this.form.align || "center",
        fontWeight: this.form.bold && this.form.block_type === "text" ? "bold" : "normal",
        fontSize: this.form.double_size && this.form.block_type === "text" ? "1.6em" : "1em",
      };
    },
  },
  watch: {
    "form.content"() {
      this.refreshQr();
    },
    "form.block_type"() {
      this.refreshQr();
    },
  },
  mounted() {
    this.load();
    axios.get("/sucursals").then((r) => (this.sucursals = r.data || [])).catch(() => {});
    axios.get("/restaurant/areas").then((r) => (this.areas = r.data || [])).catch(() => {});
  },
  methods: {
    load() {
      this.loading = true;
      ticketPrintBlocksService
        .getAll()
        .then((res) => (this.blocks = res.data || []))
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => (this.loading = false));
    },
    blocksByPosition(position) {
      return this.blocks
        .filter((b) => b.position === position)
        .filter((b) => {
          if (!this.filterSucursal) return true;
          const ids = this.parseArr(b.applies_sucursales);
          return ids.length === 0 || ids.includes(this.filterSucursal);
        })
        .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    },
    typeLabel(t) {
      const o = this.typeOptions.find((x) => x.value === t);
      return o ? o.text : t;
    },
    summary(b) {
      if (b.block_type === "image") return this.str(b.image_url);
      if (b.block_type === "separator" || b.block_type === "blank") return "";
      return this.str(b.content);
    },
    appliesLabel(b) {
      const suc = this.parseArr(b.applies_sucursales);
      const areas = this.parseArr(b.applies_areas);
      let label = "Todas las sucursales";
      if (suc.length) {
        const names = suc.map((id) => {
          const s = this.sucursals.find((x) => x.id === id);
          return s ? s.name || s.nombre : "#" + id;
        });
        label = suc.length <= 3 ? names.join(", ") : suc.length + " sucursales";
      }
      if (areas.length) label += " · " + areas.length + " área(s)";
      return label;
    },
    openNew() {
      this.form = emptyForm();
      this.$bvModal.show("modal-print-block");
    },
    openEdit(b) {
      this.form = {
        ...emptyForm(),
        ...b,
        content: this.str(b.content),
        caption: this.str(b.caption),
        image_url: this.str(b.image_url),
        image_hash: this.str(b.image_hash),
        fallback_text: this.str(b.fallback_text),
        sucursalIds: this.parseArr(b.applies_sucursales),
        areaIds: this.parseArr(b.applies_areas),
      };
      this.$bvModal.show("modal-print-block");
    },
    save() {
      const f = this.form;
      const payload = {
        name: f.name,
        block_type: f.block_type,
        position: f.position,
        sort_order: Number(f.sort_order) || 0,
        content: f.content || null,
        caption: f.caption || null,
        image_url: f.block_type === "image" ? f.image_url || null : null,
        image_hash: f.block_type === "image" ? f.image_hash || null : null,
        fallback_text: f.fallback_text || null,
        align: f.align,
        bold: f.bold,
        double_size: f.double_size,
        print_condition: f.print_condition,
        applies_sucursales: f.sucursalIds.length ? JSON.stringify(f.sucursalIds) : null,
        applies_areas: f.areaIds.length ? JSON.stringify(f.areaIds) : null,
      };
      this.saving = true;
      const req = f.id ? ticketPrintBlocksService.update(f.id, payload) : ticketPrintBlocksService.create(payload);
      req
        .then(() => {
          this.makeToast("Guardado", "Bloque guardado", "success");
          this.$bvModal.hide("modal-print-block");
          this.load();
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => (this.saving = false));
    },
    remove(b) {
      this.$bvModal
        .msgBoxConfirm("¿Eliminar el bloque \"" + b.name + "\"?", { okVariant: "danger", okTitle: "Eliminar", cancelTitle: "Cancelar" })
        .then((ok) => {
          if (!ok) return;
          ticketPrintBlocksService
            .delete(b.id)
            .then(() => {
              this.makeToast("Eliminado", "Bloque eliminado", "success");
              this.load();
            })
            .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
        });
    },
    uploadImage(file) {
      if (!file) return;
      this.uploading = true;
      ticketPrintBlocksService
        .uploadImage(file)
        .then((res) => {
          this.form.image_url = res.data.image_url;
          this.form.image_hash = res.data.image_hash;
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => (this.uploading = false));
    },
    insertPlaceholder(key) {
      this.form.content = (this.form.content || "") + key;
    },
    resolve(text, urlEncode) {
      let out = text || "";
      for (const ph of this.placeholders) {
        const v = urlEncode ? encodeURIComponent(ph.sample) : ph.sample;
        out = out.split(ph.key).join(v);
      }
      return out;
    },
    refreshQr() {
      if (this.form.block_type !== "qr" || !this.form.content) {
        this.qrPreview = "";
        return;
      }
      QRCode.toDataURL(this.resolve(this.form.content, true), { margin: 1, width: 160 })
        .then((url) => (this.qrPreview = url))
        .catch(() => (this.qrPreview = ""));
    },
    // Los campos nullable del backend llegan como string o como {String, Valid}.
    str(v) {
      if (v && typeof v === "object") return v.Valid === false ? "" : v.String || "";
      return v || "";
    },
    parseArr(v) {
      const s = this.str(v);
      if (!s) return [];
      try {
        const arr = JSON.parse(s);
        return Array.isArray(arr) ? arr.map((n) => Number(n)) : [];
      } catch (e) {
        return [];
      }
    },
    errMsg(err) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string" ? err.response.data : err.response.data.error || JSON.stringify(err.response.data);
      }
      return err && err.message ? err.message : "Error desconocido";
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, autoHideDelay: 5000, appendToast: true });
    },
  },
};
</script>

<style scoped>
.ticket-preview {
  font-family: monospace;
  border: 1px dashed #999;
  background: #fff;
  color: #000;
  padding: 8px;
  max-width: 100%;
  overflow-wrap: anywhere;
}
.pre {
  white-space: pre-wrap;
}
.qr-preview {
  width: 140px;
}
.img-preview {
  max-width: 100%;
  max-height: 200px;
}
.placeholder-chip {
  cursor: pointer;
  border: 1px solid #ccc;
}
</style>
